import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

function InfoPanel({ conversation, agents, onAssign, onUpdatePartner, onUpdateStatus, onAssignOdooUser, onAssignOdooTeam, onDeleteTicket, onRefresh }) {
  const [partners, setPartners] = useState([]);
  const [searchPartner, setSearchPartner] = useState('');
  const [showPartnerSearch, setShowPartnerSearch] = useState(false);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [stages, setStages] = useState([]);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [odooUsers, setOdooUsers] = useState([]);
  const [odooTeams, setOdooTeams] = useState([]);
  const [assigningUser, setAssigningUser] = useState(false);
  const [assigningTeam, setAssigningTeam] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showSpamConfirm, setShowSpamConfirm] = useState(false);
  const [spamming, setSpamming] = useState(false);
  const [ticketViews, setTicketViews] = useState([]);
  const [showViews, setShowViews] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [contactFirstname, setContactFirstname] = useState('');

  // Statuts pour les tickets WhatsApp
  const whatsappStatuses = [
    { id: 'nouveau', name: 'Nouveau', color: '#3b82f6' },
    { id: 'en cours', name: 'En cours', color: '#f59e0b' },
    { id: 'en attente', name: 'En attente', color: '#8b5cf6' },
    { id: 'résolu', name: 'Résolu', color: '#22c55e' },
    { id: 'fermé', name: 'Fermé', color: '#6b7280' }
  ];

  // Charger les statuts, utilisateurs et équipes au montage
  useEffect(() => {
    if (conversation.source === 'odoo') {
      fetchStages();
    }
    fetchOdooUsers();
    fetchOdooTeams();
    fetchTicketViews();
  }, [conversation.source, conversation.id]);

  const fetchStages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/odoo/stages`);
      if (res.ok) {
        const data = await res.json();
        // Trier par séquence
        data.sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        setStages(data);
      }
    } catch (e) {
      console.error('Erreur chargement statuts:', e);
    }
  };

  const fetchOdooUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/odoo/users`);
      if (res.ok) {
        const data = await res.json();
        setOdooUsers(data);
      }
    } catch (e) {
      console.error('Erreur chargement utilisateurs Odoo:', e);
    }
  };

  const fetchOdooTeams = async () => {
    try {
      const res = await fetch(`${API_URL}/api/odoo/teams`);
      if (res.ok) {
        const data = await res.json();
        setOdooTeams(data);
      }
    } catch (e) {
      console.error('Erreur chargement équipes Odoo:', e);
    }
  };

  const fetchTicketViews = async () => {
    try {
      const type = conversation.source;
      const id = conversation.waTicketId || conversation.id?.replace('odoo_', '').replace('wa_', '');
      const res = await fetch(`${API_URL}/api/tickets/${type}/${id}/views`);
      if (res.ok) {
        const data = await res.json();
        setTicketViews(data);
      }
    } catch (e) {
      console.error('Erreur chargement vues ticket:', e);
    }
  };

  // Mettre à jour le statut d'un ticket WhatsApp
  const handleWhatsAppStatusChange = async (status) => {
    if (!status || updatingStatus || !conversation.waTicketId) return;

    setUpdatingStatus(true);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/tickets/${conversation.waTicketId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (res.ok && onRefresh) {
        onRefresh();
      }
    } catch (e) {
      console.error('Erreur changement statut WhatsApp:', e);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Assigner un ticket WhatsApp à un utilisateur
  const handleWhatsAppAssignUser = async (userId, userName) => {
    if (!userId || assigningUser || !conversation.waTicketId) return;

    setAssigningUser(true);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/tickets/${conversation.waTicketId}/assign-user`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: parseInt(userId), userName })
      });
      if (res.ok && onRefresh) {
        onRefresh();
      }
    } catch (e) {
      console.error('Erreur assignation utilisateur WhatsApp:', e);
    } finally {
      setAssigningUser(false);
    }
  };

  // Assigner un ticket WhatsApp à une équipe
  const handleWhatsAppAssignTeam = async (teamId, teamName) => {
    if (!teamId || assigningTeam || !conversation.waTicketId) return;

    setAssigningTeam(true);
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/tickets/${conversation.waTicketId}/assign-team`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId: parseInt(teamId), teamName })
      });
      if (res.ok && onRefresh) {
        onRefresh();
      }
    } catch (e) {
      console.error('Erreur assignation équipe WhatsApp:', e);
    } finally {
      setAssigningTeam(false);
    }
  };

  const handleAssignOdooUser = async (userId) => {
    if (!userId || assigningUser) return;

    setAssigningUser(true);
    try {
      const ticketId = conversation.id?.replace('odoo_', '');
      const res = await fetch(`${API_URL}/api/odoo/tickets/${ticketId}/assign-user`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: parseInt(userId) })
      });
      if (res.ok && onAssignOdooUser) {
        onAssignOdooUser(parseInt(userId));
      }
    } catch (e) {
      console.error('Erreur assignation utilisateur:', e);
    } finally {
      setAssigningUser(false);
    }
  };

  const handleAssignOdooTeam = async (teamId) => {
    if (!teamId || assigningTeam) return;

    setAssigningTeam(true);
    try {
      const ticketId = conversation.id?.replace('odoo_', '');
      const res = await fetch(`${API_URL}/api/odoo/tickets/${ticketId}/assign-team`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId: parseInt(teamId) })
      });
      if (res.ok && onAssignOdooTeam) {
        onAssignOdooTeam(parseInt(teamId));
      }
    } catch (e) {
      console.error('Erreur assignation équipe:', e);
    } finally {
      setAssigningTeam(false);
    }
  };

  const handleStatusChange = async (stageId) => {
    if (!stageId || updatingStatus) return;

    setUpdatingStatus(true);
    try {
      if (onUpdateStatus) {
        await onUpdateStatus(parseInt(stageId));
      }
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityLabel = (priority) => {
    const priorities = {
      '0': 'Basse',
      '1': 'Normale',
      '2': 'Haute',
      '3': 'Urgente'
    };
    return priorities[priority] || 'Normale';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      '0': '#4ade80',
      '1': '#60a5fa',
      '2': '#fbbf24',
      '3': '#ef4444'
    };
    return colors[priority] || '#60a5fa';
  };

  // Rechercher des partenaires dans Odoo
  const searchPartners = async (query) => {
    if (!query || query.length < 2) {
      setPartners([]);
      return;
    }

    setLoadingPartners(true);
    try {
      const res = await fetch(`${API_URL}/api/odoo/partners/search?q=${encodeURIComponent(query)}`);
      if (res.ok) {
        const data = await res.json();
        setPartners(data);
      }
    } catch (e) {
      console.error('Erreur recherche partenaires:', e);
    } finally {
      setLoadingPartners(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchPartner) {
        searchPartners(searchPartner);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchPartner]);

  const handleSelectPartner = async (partner) => {
    if (conversation.source === 'whatsapp' && conversation.waTicketId) {
      // Pour WhatsApp: afficher l'étape de confirmation avec champ prénom
      setSelectedPartner(partner);
      setContactFirstname('');
    } else if (conversation.source === 'odoo' && partner.is_company) {
      // Pour Odoo + société: afficher confirmation de création sous-contact
      setSelectedPartner(partner);
    } else if (onUpdatePartner) {
      // Pour Odoo + individu: mise à jour directe
      await onUpdatePartner(partner.id, { isCompany: false });
      setShowPartnerSearch(false);
      setSearchPartner('');
      setPartners([]);
    }
  };

  const handleConfirmOdooCompanyLink = async () => {
    if (!selectedPartner || !onUpdatePartner) return;

    await onUpdatePartner(selectedPartner.id, {
      isCompany: true,
      ticketEmail: conversation.partnerEmail,
      ticketName: conversation.customer
    });

    setSelectedPartner(null);
    setShowPartnerSearch(false);
    setSearchPartner('');
    setPartners([]);
  };

  const handleConfirmLinkPartner = async () => {
    if (!selectedPartner || !conversation.waTicketId) return;
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/tickets/${conversation.waTicketId}/link-partner`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          partnerId: selectedPartner.id,
          partnerName: selectedPartner.name,
          partnerEmail: selectedPartner.email || null,
          partnerPhone: selectedPartner.phone || selectedPartner.mobile || null,
          contactFirstname: contactFirstname.trim() || null
        })
      });
      if (res.ok && onRefresh) {
        onRefresh();
      }
    } catch (e) {
      console.error('Erreur liaison partenaire WhatsApp:', e);
    }
    setSelectedPartner(null);
    setContactFirstname('');
    setShowPartnerSearch(false);
    setSearchPartner('');
    setPartners([]);
  };

  const ticketId = conversation.id?.replace('odoo_', '');

  return (
    <div className="info-panel">
      <div className="info-section">
        <h3>Informations</h3>

        <div className="info-item">
          <span className="info-label">Source</span>
          <span className={`conversation-source ${conversation.source}`}>
            {conversation.source === 'odoo' ? 'Odoo' : 'WhatsApp'}
          </span>
        </div>

        <div className="info-item">
          <span className="info-label">Statut</span>
          {conversation.source === 'odoo' && stages.length > 0 ? (
            <select
              value={conversation.stageId || ''}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={updatingStatus}
              style={{
                padding: '6px 10px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--text-primary)',
                fontSize: '0.85rem',
                cursor: updatingStatus ? 'wait' : 'pointer',
                minWidth: '120px'
              }}
            >
              {stages.map(stage => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
          ) : conversation.source === 'whatsapp' && conversation.waTicketId ? (
            <select
              value={conversation.status || 'nouveau'}
              onChange={(e) => handleWhatsAppStatusChange(e.target.value)}
              disabled={updatingStatus}
              style={{
                padding: '6px 10px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border)',
                borderRadius: '6px',
                color: 'var(--text-primary)',
                fontSize: '0.85rem',
                cursor: updatingStatus ? 'wait' : 'pointer',
                minWidth: '120px'
              }}
            >
              {whatsappStatuses.map(status => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </select>
          ) : (
            <span className={`status-badge ${conversation.status?.toLowerCase().replace(' ', '-')}`}>
              {conversation.status}
            </span>
          )}
        </div>

        <div className="info-item">
          <span className="info-label">Priorité</span>
          <span style={{ color: getPriorityColor(conversation.priority) }}>
            {getPriorityLabel(conversation.priority)}
          </span>
        </div>

        <div className="info-item">
          <span className="info-label">Mise à jour</span>
          <span>{formatDate(conversation.updatedAt)}</span>
        </div>
      </div>

      {/* Section Client */}
      <div className="info-section">
        <h3>Client</h3>

        <div style={{
          padding: '12px',
          background: 'var(--bg-primary)',
          borderRadius: '8px',
          marginBottom: '10px'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>
            {conversation.customer || 'Non défini'}
          </div>
          {conversation.partnerEmail && (
            <div style={{ fontSize: '0.85rem', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: 'var(--text-muted)' }}>📧</span>
              <a
                href={`mailto:${conversation.partnerEmail}`}
                style={{ color: 'var(--accent)', textDecoration: 'none' }}
              >
                {conversation.partnerEmail}
              </a>
            </div>
          )}
          {conversation.partnerPhone && (
            <div style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ color: 'var(--text-muted)' }}>📞</span>
              <a
                href={`tel:${conversation.partnerPhone}`}
                style={{ color: 'var(--accent)', textDecoration: 'none' }}
              >
                {conversation.partnerPhone}
              </a>
            </div>
          )}
          {!conversation.partnerEmail && !conversation.partnerPhone && (
            <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Aucune coordonnée
            </div>
          )}
        </div>

        {/* Recherche de client Odoo - disponible pour Odoo ET WhatsApp */}
        {(conversation.source === 'odoo' || (conversation.source === 'whatsapp' && conversation.waTicketId)) && (
          <>
            <button
              className="btn btn-secondary"
              style={{ width: '100%', justifyContent: 'center', marginBottom: '10px' }}
              onClick={() => setShowPartnerSearch(!showPartnerSearch)}
            >
              {showPartnerSearch ? '✕ Annuler' : conversation.source === 'odoo' ? '👤 Modifier le client' : '🔗 Lier à un client Odoo'}
            </button>

            {showPartnerSearch && (
              <div style={{ marginTop: '10px' }}>
                <input
                  type="text"
                  placeholder="Rechercher un client Odoo..."
                  value={searchPartner}
                  onChange={(e) => setSearchPartner(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    color: 'var(--text-primary)',
                    fontSize: '0.9rem',
                    marginBottom: '10px'
                  }}
                />

                {loadingPartners && (
                  <div style={{ padding: '10px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    Recherche...
                  </div>
                )}

                {partners.length > 0 && !selectedPartner && (
                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid var(--border)',
                    borderRadius: '6px'
                  }}>
                    {partners.map(p => (
                      <div
                        key={p.id}
                        onClick={() => handleSelectPartner(p)}
                        style={{
                          padding: '10px',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--border)',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '0.9rem' }}>
                            {p.is_company ? '\uD83C\uDFE2' : '\uD83D\uDC64'}
                          </span>
                          <span style={{ fontWeight: '500' }}>{p.name}</span>
                          {p.is_company && (
                            <span style={{
                              fontSize: '0.65rem',
                              background: 'var(--accent)',
                              color: 'white',
                              padding: '1px 6px',
                              borderRadius: '4px'
                            }}>
                              Société
                            </span>
                          )}
                        </div>
                        {p.email && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: '22px' }}>
                            {p.email}
                          </div>
                        )}
                        {p.parent_id && p.parent_id[1] && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--accent)', marginLeft: '22px' }}>
                            Sous-contact de : {p.parent_id[1]}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {searchPartner.length >= 2 && !loadingPartners && partners.length === 0 && !selectedPartner && (
                  <div style={{ padding: '10px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    Aucun résultat
                  </div>
                )}

                {/* Étape de confirmation avec prénom pour WhatsApp */}
                {selectedPartner && conversation.source === 'whatsapp' && (
                  <div style={{
                    marginTop: '10px',
                    padding: '15px',
                    background: 'var(--bg-primary)',
                    borderRadius: '8px',
                    border: '2px solid var(--accent)'
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '10px', color: 'var(--text-primary)' }}>
                      Lier à : {selectedPartner.name}
                    </div>
                    {selectedPartner.email && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                        {selectedPartner.email}
                      </div>
                    )}
                    <div style={{ marginTop: '10px' }}>
                      <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                        Prénom du contact (optionnel)
                      </label>
                      <input
                        type="text"
                        placeholder="Ex: Pierre, Marie..."
                        value={contactFirstname}
                        onChange={(e) => setContactFirstname(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: 'var(--bg-secondary)',
                          border: '1px solid var(--border)',
                          borderRadius: '6px',
                          color: 'var(--text-primary)',
                          fontSize: '0.9rem',
                          marginBottom: '10px'
                        }}
                      />
                      {contactFirstname.trim() && (
                        <div style={{
                          fontSize: '0.8rem',
                          color: 'var(--accent)',
                          marginBottom: '10px',
                          padding: '8px',
                          background: 'rgba(13, 148, 136, 0.1)',
                          borderRadius: '6px'
                        }}>
                          Aperçu : <strong>{selectedPartner.name} {contactFirstname.trim().toUpperCase()}</strong>
                          {conversation.customerPhone && ` (${conversation.customerPhone})`}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ flex: 1 }}
                        onClick={() => { setSelectedPartner(null); setContactFirstname(''); }}
                      >
                        Annuler
                      </button>
                      <button
                        className="btn"
                        style={{ flex: 1, background: 'var(--accent)', color: 'white', border: 'none' }}
                        onClick={handleConfirmLinkPartner}
                      >
                        Confirmer
                      </button>
                    </div>
                  </div>
                )}

                {/* Étape de confirmation pour lier à une société Odoo */}
                {selectedPartner && conversation.source === 'odoo' && selectedPartner.is_company && (
                  <div style={{
                    marginTop: '10px',
                    padding: '15px',
                    background: 'var(--bg-primary)',
                    borderRadius: '8px',
                    border: '2px solid var(--accent)'
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '10px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {'\uD83C\uDFE2'} Lier à : {selectedPartner.name}
                    </div>
                    {conversation.partnerEmail ? (
                      <div style={{
                        fontSize: '0.85rem',
                        color: 'var(--text-muted)',
                        marginBottom: '12px',
                        padding: '10px',
                        background: 'rgba(13, 148, 136, 0.1)',
                        borderRadius: '6px',
                        lineHeight: '1.6'
                      }}>
                        Un sous-contact sera recherché ou créé sous <strong style={{ color: 'var(--text-primary)' }}>{selectedPartner.name}</strong> :
                        <br/>
                        {conversation.customer && <><strong>Nom :</strong> {conversation.customer}<br/></>}
                        <strong>Email :</strong> {conversation.partnerEmail}
                      </div>
                    ) : (
                      <div style={{
                        fontSize: '0.85rem',
                        color: '#f59e0b',
                        marginBottom: '12px',
                        padding: '10px',
                        background: 'rgba(245, 158, 11, 0.1)',
                        borderRadius: '6px'
                      }}>
                        Aucun email sur ce ticket — le ticket sera lié directement à la société.
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ flex: 1 }}
                        onClick={() => setSelectedPartner(null)}
                      >
                        Annuler
                      </button>
                      <button
                        className="btn"
                        style={{ flex: 1, background: 'var(--accent)', color: 'white', border: 'none' }}
                        onClick={handleConfirmOdooCompanyLink}
                      >
                        Confirmer
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Section Assignation */}
      <div className="info-section">
        <h3>Assignation</h3>

        <div className="info-item">
          <span className="info-label">Assigné à</span>
          <span>{conversation.assignedTo || 'Non assigné'}</span>
        </div>

        {conversation.team && (
          <div className="info-item">
            <span className="info-label">Équipe</span>
            <span>{conversation.team}</span>
          </div>
        )}

        {/* Assignation pour tickets Odoo */}
        {conversation.source === 'odoo' && (
          <>
            <div style={{ marginTop: '10px' }}>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                👤 Assigner à un utilisateur
              </label>
              <select
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  color: 'var(--text-primary)',
                  fontSize: '0.9rem',
                  cursor: assigningUser ? 'wait' : 'pointer'
                }}
                value=""
                onChange={(e) => handleAssignOdooUser(e.target.value)}
                disabled={assigningUser}
              >
                <option value="">Sélectionner un utilisateur...</option>
                {odooUsers.map(user => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>

            {odooTeams.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                  👥 Assigner à une équipe
                </label>
                <select
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    color: 'var(--text-primary)',
                    fontSize: '0.9rem',
                    cursor: assigningTeam ? 'wait' : 'pointer'
                  }}
                  value=""
                  onChange={(e) => handleAssignOdooTeam(e.target.value)}
                  disabled={assigningTeam}
                >
                  <option value="">Sélectionner une équipe...</option>
                  {odooTeams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {/* Assignation pour tickets WhatsApp */}
        {conversation.source === 'whatsapp' && conversation.waTicketId && (
          <>
            <div style={{ marginTop: '10px' }}>
              <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                👤 Assigner à un utilisateur
              </label>
              <select
                style={{
                  width: '100%',
                  padding: '10px',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  color: 'var(--text-primary)',
                  fontSize: '0.9rem',
                  cursor: assigningUser ? 'wait' : 'pointer'
                }}
                value=""
                onChange={(e) => {
                  const user = odooUsers.find(u => u.id === parseInt(e.target.value));
                  if (user) handleWhatsAppAssignUser(user.id, user.name);
                }}
                disabled={assigningUser}
              >
                <option value="">Sélectionner un utilisateur...</option>
                {odooUsers.map(user => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>

            {odooTeams.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
                  👥 Assigner à une équipe
                </label>
                <select
                  style={{
                    width: '100%',
                    padding: '10px',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '6px',
                    color: 'var(--text-primary)',
                    fontSize: '0.9rem',
                    cursor: assigningTeam ? 'wait' : 'pointer'
                  }}
                  value=""
                  onChange={(e) => {
                    const team = odooTeams.find(t => t.id === parseInt(e.target.value));
                    if (team) handleWhatsAppAssignTeam(team.id, team.name);
                  }}
                  disabled={assigningTeam}
                >
                  <option value="">Sélectionner une équipe...</option>
                  {odooTeams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}

        {/* Assigner à un agent local (pour les deux sources) */}
        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid var(--border)' }}>
          <label style={{ fontSize: '0.8rem', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>
            🏷️ Agent Support Hub
          </label>
          <select
            style={{
              width: '100%',
              padding: '10px',
              background: 'var(--bg-primary)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text-primary)',
              fontSize: '0.9rem'
            }}
            value=""
            onChange={(e) => {
              if (e.target.value) {
                onAssign(parseInt(e.target.value));
              }
            }}
          >
            <option value="">Assigner localement...</option>
            {agents.map(agent => (
              <option key={agent.id} value={agent.id}>{agent.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Section Tracking des vues */}
      <div className="info-section">
        <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>👁️ Vues du ticket</span>
          <button
            onClick={() => setShowViews(!showViews)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--accent)',
              cursor: 'pointer',
              fontSize: '0.8rem'
            }}
          >
            {showViews ? 'Masquer' : `Voir (${ticketViews.length})`}
          </button>
        </h3>

        {showViews && ticketViews.length > 0 && (
          <div style={{
            maxHeight: '150px',
            overflowY: 'auto',
            marginTop: '10px',
            fontSize: '0.8rem'
          }}>
            {ticketViews.slice(0, 20).map((view, i) => (
              <div key={i} style={{
                padding: '6px 8px',
                background: 'var(--bg-primary)',
                borderRadius: '4px',
                marginBottom: '4px',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontWeight: '500' }}>{view.agent_name}</span>
                <span style={{ color: 'var(--text-muted)' }}>
                  {new Date(view.viewed_at).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
            ))}
          </div>
        )}

        {showViews && ticketViews.length === 0 && (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '10px' }}>
            Aucune vue enregistrée
          </div>
        )}
      </div>

      {/* Actions Odoo */}
      {conversation.source === 'odoo' && (
        <div className="info-section">
          <h3>Actions</h3>
          <a
            href={`https://odoo.biborne.com/web#id=${ticketId}&model=helpdesk.ticket&view_type=form`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary"
            style={{ width: '100%', marginBottom: '8px', justifyContent: 'center', textDecoration: 'none' }}
          >
            🔗 Ouvrir dans Odoo
          </a>

          {conversation.partnerEmail && (
            <button
              className="btn"
              style={{
                width: '100%',
                justifyContent: 'center',
                marginBottom: '8px',
                background: '#f97316',
                color: 'white',
                border: 'none'
              }}
              onClick={() => setShowSpamConfirm(true)}
              disabled={spamming}
            >
              🚫 Spam - Bloquer l'email
            </button>
          )}

          <button
            className="btn"
            style={{
              width: '100%',
              justifyContent: 'center',
              background: '#dc2626',
              color: 'white',
              border: 'none'
            }}
            onClick={() => setShowDeleteConfirm(true)}
            disabled={deleting}
          >
            🗑️ Supprimer le ticket
          </button>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#dc2626' }}>
              ⚠️ Confirmer la suppression
            </h3>
            <p style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>
              Êtes-vous sûr de vouloir supprimer ce ticket ?
            </p>
            <p style={{
              marginBottom: '20px',
              padding: '12px',
              background: 'var(--bg-primary)',
              borderRadius: '8px',
              fontWeight: '600'
            }}>
              "{conversation.title}"
            </p>
            <p style={{
              marginBottom: '20px',
              color: '#dc2626',
              fontSize: '0.9rem'
            }}>
              Cette action est irréversible et supprimera également tous les messages associés.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
              >
                Annuler
              </button>
              <button
                className="btn"
                style={{
                  background: '#dc2626',
                  color: 'white',
                  border: 'none'
                }}
                onClick={async () => {
                  setDeleting(true);
                  try {
                    const res = await fetch(`${API_URL}/api/odoo/tickets/${ticketId}`, {
                      method: 'DELETE',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({})
                    });
                    if (res.ok) {
                      setShowDeleteConfirm(false);
                      if (onDeleteTicket) {
                        onDeleteTicket(conversation.id);
                      }
                    } else {
                      alert('Erreur lors de la suppression');
                    }
                  } catch (e) {
                    console.error('Erreur suppression:', e);
                    alert('Erreur lors de la suppression');
                  } finally {
                    setDeleting(false);
                  }
                }}
                disabled={deleting}
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal de confirmation spam */}
      {showSpamConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#f97316' }}>
              🚫 Marquer comme Spam
            </h3>
            <p style={{ marginBottom: '8px', color: 'var(--text-primary)' }}>
              Cette action va :
            </p>
            <ul style={{ marginBottom: '16px', paddingLeft: '20px', color: 'var(--text-primary)', lineHeight: '1.8' }}>
              <li>Bloquer l'adresse email <strong style={{ color: '#f97316' }}>{conversation.partnerEmail}</strong> sur Odoo</li>
              <li>Supprimer ce ticket</li>
              <li>Empêcher cet expéditeur de créer de nouveaux tickets</li>
            </ul>
            <p style={{
              marginBottom: '20px',
              color: '#f97316',
              fontSize: '0.9rem'
            }}>
              L'email sera ajouté à la liste noire d'Odoo.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowSpamConfirm(false)}
                disabled={spamming}
              >
                Annuler
              </button>
              <button
                className="btn"
                style={{
                  background: '#f97316',
                  color: 'white',
                  border: 'none'
                }}
                onClick={async () => {
                  setSpamming(true);
                  try {
                    const res = await fetch(`${API_URL}/api/odoo/tickets/${ticketId}/spam`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({})
                    });
                    if (res.ok) {
                      setShowSpamConfirm(false);
                      if (onDeleteTicket) {
                        onDeleteTicket(conversation.id);
                      }
                    } else {
                      const data = await res.json();
                      alert(data.error || 'Erreur lors du blocage');
                    }
                  } catch (e) {
                    console.error('Erreur spam:', e);
                    alert('Erreur lors du blocage');
                  } finally {
                    setSpamming(false);
                  }
                }}
                disabled={spamming}
              >
                {spamming ? 'Blocage...' : '🚫 Bloquer et supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InfoPanel;
